const CANVAS_WIDTH = 360;
const CANVAS_HEIGHT = 640;
const PROBE_FPS = 8;
const FRAME_INTERVAL_MS = Math.round(1000 / PROBE_FPS);
const STABILITY_FRAMES_NEEDED = 2;
const STABILITY_POLL_MS = 120;
const ACTIVE_LIMIT_MS = 15000;
const PUMP_WATCHDOG_MS = FRAME_INTERVAL_MS * 3;
/** @type {0} ZegoStreamUpdateType.video — video only, keep mic */
const ZEGO_PUBLISH_UPDATE_VIDEO = 0;

let activePumpAttemptId = 0;
let pumpProbeInFlight = false;
/** @type {{ cancelled: boolean } | null} */
let activeRunState = null;
/** @type {ReturnType<typeof setInterval> | null} */
let pumpTimerId = null;
/** @type {ReturnType<typeof setInterval> | null} */
let watchdogTimerId = null;
/** @type {ReturnType<typeof setTimeout> | null} */
let activeLimitTimerId = null;

/** @type {{
 *   canvasStream: MediaStream | null,
 *   rawVideoOnlyStream: MediaStream | null,
 *   probeVideo: HTMLVideoElement | null,
 *   probeCanvas: HTMLCanvasElement | null,
 *   publishSwapped: boolean,
 *   zegoEngine: object | null,
 *   zegoLocalStream: object | null,
 *   terminalLogged: boolean,
 *   startedAt: number,
 *   hasActiveRun: boolean,
 *   pumpFrameCount: number,
 *   lastPumpAt: number,
 *   markerPhase: number,
 * }} */
const runtime = {
  canvasStream: null,
  rawVideoOnlyStream: null,
  probeVideo: null,
  probeCanvas: null,
  publishSwapped: false,
  zegoEngine: null,
  zegoLocalStream: null,
  terminalLogged: false,
  startedAt: 0,
  hasActiveRun: false,
  pumpFrameCount: 0,
  lastPumpAt: 0,
  markerPhase: 0,
};

function isZegoRawPumpProbeEnabled() {
  const raw = String(process.env.REACT_APP_ZEGO_RAW_PUMP_PROBE || "")
    .trim()
    .toLowerCase();
  return raw === "true";
}

function logTerminal(message) {
  if (runtime.terminalLogged) {
    return;
  }
  runtime.terminalLogged = true;
  console.log(message);
}

function throwIfCancelled(runState, attemptId) {
  if (runState.cancelled || attemptId !== activePumpAttemptId) {
    throw Object.assign(new Error("aborted"), { code: "ABORTED" });
  }
}

function clearPumpTimer() {
  if (pumpTimerId) {
    clearInterval(pumpTimerId);
    pumpTimerId = null;
  }
}

function clearWatchdogTimer() {
  if (watchdogTimerId) {
    clearInterval(watchdogTimerId);
    watchdogTimerId = null;
  }
}

function clearActiveLimitTimer() {
  if (activeLimitTimerId) {
    clearTimeout(activeLimitTimerId);
    activeLimitTimerId = null;
  }
}

function stopCanvasStreamTracks() {
  runtime.canvasStream?.getVideoTracks?.().forEach((track) => {
    try {
      track.stop();
    } catch {
      // ignore
    }
  });
  runtime.canvasStream = null;
}

function removeProbeElements() {
  if (runtime.probeVideo) {
    try {
      runtime.probeVideo.pause();
    } catch {
      // ignore
    }
    runtime.probeVideo.srcObject = null;
    runtime.probeVideo.remove();
    runtime.probeVideo = null;
  }

  if (runtime.probeCanvas) {
    runtime.probeCanvas.remove();
    runtime.probeCanvas = null;
  }
}

/**
 * @param {object | null | undefined} zegoLocalStream
 * @returns {MediaStream | null}
 */
function extractZegoInputMediaStream(zegoLocalStream) {
  if (!zegoLocalStream) {
    return null;
  }

  const wrappedStream = zegoLocalStream.stream;
  if (wrappedStream?.getVideoTracks?.()?.length) {
    return wrappedStream;
  }

  const capture = zegoLocalStream.videoCaptureStream;
  if (capture?.getVideoTracks?.()?.length) {
    return capture;
  }

  const track = zegoLocalStream.getVideoTracks?.()?.[0];
  if (track) {
    return new MediaStream([track]);
  }

  return null;
}

/**
 * @param {MediaStream | null} originalMediaStream
 * @returns {MediaStream | null}
 */
function createRawVideoOnlyStream(originalMediaStream) {
  const videoTracks = originalMediaStream?.getVideoTracks?.() || [];
  if (!videoTracks.length) {
    return null;
  }
  return new MediaStream(videoTracks);
}

function isZegoCustomVideoSwapSupported(zegoLocalStream, zegoEngine) {
  return (
    typeof zegoLocalStream?.startCaptureCustomVideo === "function" &&
    typeof zegoEngine?.updatePublishingStream === "function"
  );
}

function isRawRestoreSupported(zegoLocalStream, zegoEngine, rawVideoOnlyStream) {
  return (
    isZegoCustomVideoSwapSupported(zegoLocalStream, zegoEngine) &&
    Boolean(rawVideoOnlyStream?.getVideoTracks?.()?.length)
  );
}

function hasProbeCleanupWork() {
  return (
    runtime.hasActiveRun ||
    runtime.publishSwapped ||
    Boolean(runtime.canvasStream || runtime.probeVideo || runtime.probeCanvas) ||
    pumpProbeInFlight ||
    Boolean(pumpTimerId || watchdogTimerId || activeLimitTimerId)
  );
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {HTMLVideoElement} video
 */
function drawPumpFrame(canvas, video) {
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return false;
  }

  const vw = video.videoWidth || CANVAS_WIDTH;
  const vh = video.videoHeight || CANVAS_HEIGHT;
  if (vw <= 0 || vh <= 0) {
    return false;
  }

  const scale = Math.max(CANVAS_WIDTH / vw, CANVAS_HEIGHT / vh);
  const drawW = vw * scale;
  const drawH = vh * scale;
  const dx = (CANVAS_WIDTH - drawW) / 2;
  const dy = (CANVAS_HEIGHT - drawH) / 2;

  ctx.drawImage(video, dx, dy, drawW, drawH);

  runtime.markerPhase = (runtime.markerPhase + 1) % 16;
  const markerSize = 14;
  const markerX = 8 + runtime.markerPhase * 4;
  const markerY = CANVAS_HEIGHT - markerSize - 8;
  ctx.fillStyle = "#00ff66";
  ctx.fillRect(markerX, markerY, markerSize, markerSize);

  const ts = new Date().toISOString().slice(11, 19);
  ctx.fillStyle = "#ffffff";
  ctx.font = "12px monospace";
  ctx.fillText(ts, 8, 16);

  runtime.pumpFrameCount += 1;
  runtime.lastPumpAt = performance.now();
  return true;
}

function startCanvasPump(video, canvas) {
  clearPumpTimer();
  runtime.pumpFrameCount = 0;
  runtime.lastPumpAt = performance.now();
  runtime.markerPhase = 0;

  pumpTimerId = setInterval(() => {
    if (!runtime.probeVideo || !runtime.probeCanvas) {
      return;
    }
    drawPumpFrame(runtime.probeCanvas, runtime.probeVideo);
  }, FRAME_INTERVAL_MS);

  drawPumpFrame(canvas, video);
  console.log("[zegoRawPumpProbe] canvasPumpStarted=true");
}

function startPumpWatchdog(zegoLocalStream, zegoEngine) {
  clearWatchdogTimer();
  watchdogTimerId = setInterval(() => {
    if (!runtime.publishSwapped) {
      return;
    }

    const videoTrack = runtime.probeVideo?.srcObject?.getVideoTracks?.()?.[0];
    const canvasTrack = runtime.canvasStream?.getVideoTracks?.()?.[0];
    const pumpStale =
      runtime.lastPumpAt > 0 && performance.now() - runtime.lastPumpAt > PUMP_WATCHDOG_MS;
    const sourceDead =
      !videoTrack ||
      videoTrack.readyState !== "live" ||
      videoTrack.muted ||
      !canvasTrack ||
      canvasTrack.readyState !== "live";

    if (pumpStale || sourceDead) {
      void restoreAndFinish(zegoLocalStream, zegoEngine, "watchdog");
    }
  }, FRAME_INTERVAL_MS);
}

/**
 * @param {MediaStream} stream
 * @param {number} neededFrames
 * @param {number} timeoutMs
 * @param {{ cancelled: boolean }} runState
 * @param {number} attemptId
 */
async function waitForCanvasStreamFrames(stream, neededFrames, timeoutMs, runState, attemptId) {
  const track = stream?.getVideoTracks?.()?.[0];
  if (!track || track.readyState !== "live") {
    return false;
  }

  const deadline = performance.now() + timeoutMs;
  let frameCount = 0;

  return new Promise((resolve) => {
    let settled = false;
    let pollId = null;

    const finish = (ok) => {
      if (settled) {
        return;
      }
      settled = true;
      if (pollId) {
        clearInterval(pollId);
      }
      resolve(ok);
    };

    pollId = setInterval(() => {
      if (runState.cancelled || attemptId !== activePumpAttemptId) {
        finish(false);
        return;
      }
      if (performance.now() > deadline) {
        finish(false);
        return;
      }

      const liveTrack = stream?.getVideoTracks?.()?.[0];
      if (!liveTrack || liveTrack.readyState !== "live") {
        finish(false);
        return;
      }

      if (runtime.pumpFrameCount >= neededFrames) {
        frameCount = runtime.pumpFrameCount;
        if (frameCount >= neededFrames) {
          finish(true);
        }
      }
    }, STABILITY_POLL_MS);
  });
}

/**
 * @param {object} zegoLocalStream
 * @param {object} zegoEngine
 * @returns {Promise<boolean>}
 */
async function restoreRawZegoVideo(zegoLocalStream, zegoEngine) {
  if (!runtime.publishSwapped || !zegoLocalStream || !zegoEngine) {
    return false;
  }

  const rawVideoOnlyStream = runtime.rawVideoOnlyStream;
  if (!isRawRestoreSupported(zegoLocalStream, zegoEngine, rawVideoOnlyStream)) {
    runtime.publishSwapped = false;
    return false;
  }

  try {
    await zegoLocalStream.startCaptureCustomVideo({
      source: rawVideoOnlyStream,
      optimizationMode: "motion",
    });
    await zegoEngine.updatePublishingStream(zegoLocalStream, ZEGO_PUBLISH_UPDATE_VIDEO);
    runtime.publishSwapped = false;
    return true;
  } catch {
    runtime.publishSwapped = false;
    return false;
  }
}

async function destroyProbeResources() {
  clearPumpTimer();
  clearWatchdogTimer();
  stopCanvasStreamTracks();
  removeProbeElements();
}

async function restoreAndFinish(zegoLocalStream, zegoEngine, reason) {
  clearActiveLimitTimer();
  const restored = runtime.publishSwapped
    ? await restoreRawZegoVideo(zegoLocalStream, zegoEngine)
    : false;
  await destroyProbeResources();

  if (reason === "success") {
    const ms = Math.round(performance.now() - runtime.startedAt);
    logTerminal(`[zegoRawPumpProbe] success ms=${ms}`);
    logTerminal(`[zegoRawPumpProbe] restoredRaw=${restored}`);
  } else if (reason === "watchdog" && !runtime.terminalLogged) {
    logTerminal("[zegoRawPumpProbe] failed reason=pump_watchdog");
    logTerminal(`[zegoRawPumpProbe] restoredRaw=${restored}`);
  }

  pumpProbeInFlight = false;
  runtime.hasActiveRun = false;
  runtime.zegoEngine = null;
  runtime.zegoLocalStream = null;
  runtime.rawVideoOnlyStream = null;
  if (activeRunState) {
    activeRunState.cancelled = true;
    activeRunState = null;
  }
}

/**
 * @param {string} [_reason]
 */
export async function cleanupZegoRawPumpProbe(_reason = "unknown") {
  if (!hasProbeCleanupWork()) {
    return;
  }

  clearActiveLimitTimer();

  const zegoLocalStream = runtime.zegoLocalStream;
  const zegoEngine = runtime.zegoEngine;
  const hadSwap = runtime.publishSwapped;

  let restored = false;
  if (hadSwap && zegoLocalStream && zegoEngine) {
    restored = await restoreRawZegoVideo(zegoLocalStream, zegoEngine);
  }

  await destroyProbeResources();

  runtime.zegoEngine = null;
  runtime.zegoLocalStream = null;
  runtime.rawVideoOnlyStream = null;
  runtime.hasActiveRun = false;
  pumpProbeInFlight = false;

  if (!runtime.terminalLogged && hadSwap) {
    logTerminal(`[zegoRawPumpProbe] restoredRaw=${restored}`);
  }

  if (activeRunState) {
    activeRunState.cancelled = true;
    activeRunState = null;
  }
}

/**
 * @param {string} [_reason]
 */
export function invalidateZegoRawPumpProbe(_reason = "unknown") {
  activePumpAttemptId += 1;
  if (activeRunState) {
    activeRunState.cancelled = true;
  }
  clearPumpTimer();
  clearWatchdogTimer();
  clearActiveLimitTimer();
}

/**
 * @param {{
 *   callId?: string,
 *   zegoEngine?: object,
 *   zegoLocalStream?: object,
 *   streamID?: string,
 * }} params
 */
export async function runZegoRawPumpProbe(params = {}) {
  try {
    const enabled = isZegoRawPumpProbeEnabled();
    console.log(`[zegoRawPumpProbe] enabled=${enabled}`);
    if (!enabled) {
      return;
    }

    if (pumpProbeInFlight) {
      return;
    }

    const { zegoEngine, zegoLocalStream } = params;
    if (!zegoEngine || !zegoLocalStream) {
      console.log("[zegoRawPumpProbe] sourceResolved=false");
      console.log("[zegoRawPumpProbe] zegoVideoSwapSupported=false");
      console.log("[zegoRawPumpProbe] zegoVideoSwapSuccess=false");
      logTerminal("[zegoRawPumpProbe] failed reason=zego_context_missing");
      return;
    }

    const attemptId = activePumpAttemptId;
    const runState = { cancelled: false };
    activeRunState = runState;
    pumpProbeInFlight = true;
    runtime.terminalLogged = false;
    runtime.publishSwapped = false;
    runtime.hasActiveRun = true;
    runtime.rawVideoOnlyStream = null;
    runtime.zegoEngine = zegoEngine;
    runtime.zegoLocalStream = zegoLocalStream;
    runtime.startedAt = performance.now();

    const originalMediaStream = extractZegoInputMediaStream(zegoLocalStream);
    const sourceResolved = Boolean(originalMediaStream?.getVideoTracks?.()?.length);
    console.log(`[zegoRawPumpProbe] sourceResolved=${sourceResolved}`);

    const zegoVideoSwapSupported = isZegoCustomVideoSwapSupported(zegoLocalStream, zegoEngine);
    console.log(`[zegoRawPumpProbe] zegoVideoSwapSupported=${zegoVideoSwapSupported}`);

    if (!sourceResolved) {
      console.log("[zegoRawPumpProbe] zegoVideoSwapSuccess=false");
      logTerminal("[zegoRawPumpProbe] failed reason=source_unresolved");
      pumpProbeInFlight = false;
      runtime.hasActiveRun = false;
      activeRunState = null;
      return;
    }

    runtime.rawVideoOnlyStream = createRawVideoOnlyStream(originalMediaStream);
    if (!isRawRestoreSupported(zegoLocalStream, zegoEngine, runtime.rawVideoOnlyStream)) {
      console.log("[zegoRawPumpProbe] zegoVideoSwapSuccess=false");
      logTerminal("[zegoRawPumpProbe] failed reason=raw_restore_unsupported");
      pumpProbeInFlight = false;
      runtime.hasActiveRun = false;
      runtime.rawVideoOnlyStream = null;
      activeRunState = null;
      return;
    }

    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.setAttribute("playsinline", "true");
    video.style.cssText =
      "position:fixed;opacity:0;pointer-events:none;width:1px;height:1px;left:-9999px;top:-9999px;";
    document.body.appendChild(video);
    runtime.probeVideo = video;
    video.srcObject = originalMediaStream;

    try {
      await video.play();
    } catch {
      console.log("[zegoRawPumpProbe] zegoVideoSwapSuccess=false");
      logTerminal("[zegoRawPumpProbe] failed reason=video_play_failed");
      removeProbeElements();
      pumpProbeInFlight = false;
      runtime.hasActiveRun = false;
      runtime.rawVideoOnlyStream = null;
      activeRunState = null;
      return;
    }

    throwIfCancelled(runState, attemptId);

    const canvas = document.createElement("canvas");
    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;
    canvas.style.cssText =
      "position:fixed;opacity:0;pointer-events:none;width:1px;height:1px;left:-9999px;top:-9999px;";
    document.body.appendChild(canvas);
    runtime.probeCanvas = canvas;

    startCanvasPump(video, canvas);

    const canvasStream = canvas.captureStream(PROBE_FPS);
    runtime.canvasStream = canvasStream;
    console.log("[zegoRawPumpProbe] canvasStreamReady=true");

    const framesStable = await waitForCanvasStreamFrames(
      canvasStream,
      STABILITY_FRAMES_NEEDED,
      3000,
      runState,
      attemptId
    );

    throwIfCancelled(runState, attemptId);

    if (!framesStable) {
      console.log("[zegoRawPumpProbe] zegoVideoSwapSuccess=false");
      logTerminal("[zegoRawPumpProbe] failed reason=canvas_stream_unstable");
      await destroyProbeResources();
      pumpProbeInFlight = false;
      runtime.hasActiveRun = false;
      runtime.rawVideoOnlyStream = null;
      activeRunState = null;
      return;
    }

    if (!zegoVideoSwapSupported) {
      console.log("[zegoRawPumpProbe] zegoVideoSwapSuccess=false");
      logTerminal("[zegoRawPumpProbe] failed reason=zego_swap_unsupported");
      await destroyProbeResources();
      pumpProbeInFlight = false;
      runtime.hasActiveRun = false;
      runtime.rawVideoOnlyStream = null;
      activeRunState = null;
      return;
    }

    let swapSuccess = false;
    try {
      await zegoLocalStream.startCaptureCustomVideo({
        source: canvasStream,
        optimizationMode: "motion",
      });
      await zegoEngine.updatePublishingStream(zegoLocalStream, ZEGO_PUBLISH_UPDATE_VIDEO);
      runtime.publishSwapped = true;
      swapSuccess = true;
    } catch {
      swapSuccess = false;
    }

    console.log(`[zegoRawPumpProbe] zegoVideoSwapSuccess=${swapSuccess}`);

    if (!swapSuccess) {
      logTerminal("[zegoRawPumpProbe] failed reason=zego_swap_failed");
      await destroyProbeResources();
      pumpProbeInFlight = false;
      runtime.hasActiveRun = false;
      runtime.rawVideoOnlyStream = null;
      activeRunState = null;
      return;
    }

    startPumpWatchdog(zegoLocalStream, zegoEngine);

    activeLimitTimerId = setTimeout(() => {
      activeLimitTimerId = null;
      void restoreAndFinish(zegoLocalStream, zegoEngine, "success");
    }, ACTIVE_LIMIT_MS);
  } catch (err) {
    if (err?.code !== "ABORTED") {
      console.log("[zegoRawPumpProbe] zegoVideoSwapSuccess=false");
      logTerminal("[zegoRawPumpProbe] failed reason=unexpected");
    }
    await destroyProbeResources();
    if (runtime.publishSwapped && runtime.zegoLocalStream && runtime.zegoEngine) {
      const restored = await restoreRawZegoVideo(runtime.zegoLocalStream, runtime.zegoEngine);
      logTerminal(`[zegoRawPumpProbe] restoredRaw=${restored}`);
    }
    pumpProbeInFlight = false;
    runtime.hasActiveRun = false;
    runtime.zegoEngine = null;
    runtime.zegoLocalStream = null;
    runtime.rawVideoOnlyStream = null;
    activeRunState = null;
  }
}
